import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { BackgroundField, BACKGROUND_AI_SCHEMA, DEFAULT_BACKGROUND } from '../fields';
import type { BackgroundValue } from '../fields';
import {
  BACKGROUND_OPACITY_FIELD,
  DEFAULT_BACKGROUND_OPACITY,
  backgroundStyleWithOpacity,
  backgroundValueColor,
  resolveBackgroundOpacityFields,
} from '../fields/background-opacity';
import { BORDER_COLOR_AI_SCHEMA, borderStyle, DEFAULT_BORDER } from '../fields/border';
import type { BorderColor, BorderValue } from '../fields/border';
import { CONTAINER_TEXT_COLOR_AI_SCHEMA, containerColorStyle, INHERIT_COLOR } from '../fields/container-colors';
import { createSpacingFields, PX_AI_SCHEMA } from '../fields/spacing-fields';
import { BLOCK_SPACING, BLOCK_WIDTHS, CONTAINER_WIDTHS, lookup, spacingToCss } from '../fields/tokens';
import type { BlockSpacing, BoxSpacing } from '../fields/tokens';
import { BorderColorField } from '../fields/widgets/border-color-field';
import { ContainerTextColorField } from '../fields/widgets/container-text-color-field';
import { NumberField } from '../fields/widgets/number-field';
import { selectField } from '../fields/widgets/select-field';

import type { SlotProps } from './slot';

const COLUMN_OPTIONS = [
  { label: '1 column', value: '1' },
  { label: '2 columns', value: '2' },
  { label: '3 columns', value: '3' },
  { label: '4 columns', value: '4' },
  { label: '5 columns', value: '5' },
  { label: '6 columns', value: '6' },
] as const;

const MOBILE_COLUMN_OPTIONS = [{ label: 'Inherit desktop', value: 'inherit' }, ...COLUMN_OPTIONS] as const;

const ALIGNMENT_OPTIONS = [
  { label: 'Fill', value: 'stretch' },
  { label: 'Start', value: 'start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'end' },
] as const;

const GAP_OPTIONS: { label: string; value: BlockSpacing }[] = [
  { label: 'None (0px)', value: '0px' as BlockSpacing },
  { label: 'Extra small (8px)', value: 'xs' },
  { label: 'Small (16px)', value: 'sm' },
  { label: 'Medium (24px)', value: 'md' },
  { label: 'Large (40px)', value: 'lg' },
  { label: 'Extra large (64px)', value: 'xl' },
  { label: '2X large (96px)', value: 'xxl' },
];

const WIDTH_OPTIONS = [
  { label: 'Fit content', value: 'fit' },
  { label: 'Fill available', value: 'fill' },
] as const;

const MAX_WIDTH_OPTIONS = [
  { label: 'Narrow', value: 'narrow' },
  { label: 'Normal', value: 'normal' },
  { label: 'Wide', value: 'wide' },
  { label: 'Full width', value: 'none' },
] as const;

const DEFAULT_COLUMNS = '3';
const DEFAULT_MOBILE_COLUMNS = '1';
const DEFAULT_GAP: BlockSpacing = 'sm';
const DEFAULT_ALIGNMENT = 'stretch';
const DEFAULT_WIDTH = 'fill';
const DEFAULT_MAX_WIDTH = 'none';
const DEFAULT_SPACING: BoxSpacing = { top: '0px', right: '0px', bottom: '0px', left: '0px' };
const DEFAULT_GRID_BACKGROUND: BackgroundValue = { ...DEFAULT_BACKGROUND, color: 'none' };
const EMPTY_GRID_CONTENT: never[] = [];

const ALIGNMENT_VALUES: ReadonlySet<string> = new Set(ALIGNMENT_OPTIONS.map(({ value }) => value));
const BACKGROUND_SIZE_VALUES = new Set(['cover', 'contain', 'auto']);
const BACKGROUND_POSITION_VALUES = new Set([
  'center',
  'top',
  'bottom',
  'left',
  'right',
  'top left',
  'top right',
  'bottom left',
  'bottom right',
]);
const BACKGROUND_REPEAT_VALUES = new Set(['no-repeat', 'repeat', 'repeat-x', 'repeat-y']);
const PIXEL_LENGTH = /^\d+px$/;
const COMPLETE_HEX = /^#[0-9a-fA-F]{6}$/;

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function resolveColumns(value: unknown, fallback: number): number {
  let parsed = NaN;
  if (typeof value === 'number') {
    parsed = value;
  } else if (typeof value === 'string' && /^\d+$/.test(value)) {
    parsed = Number(value);
  }
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 6 ? parsed : fallback;
}

function resolveAlignment(value: unknown): string {
  return typeof value === 'string' && ALIGNMENT_VALUES.has(value) ? value : DEFAULT_ALIGNMENT;
}

function resolveGap(value: unknown): string {
  if (value === '0px') return '0px';
  return typeof value === 'string'
    ? lookup(BLOCK_SPACING, value, BLOCK_SPACING[DEFAULT_GAP])
    : BLOCK_SPACING[DEFAULT_GAP];
}

function resolveSpacing(value: unknown): BoxSpacing {
  if (!isRecord(value)) return DEFAULT_SPACING;

  return {
    top: typeof value.top === 'string' && PIXEL_LENGTH.test(value.top) ? value.top : DEFAULT_SPACING.top,
    right: typeof value.right === 'string' && PIXEL_LENGTH.test(value.right) ? value.right : DEFAULT_SPACING.right,
    bottom: typeof value.bottom === 'string' && PIXEL_LENGTH.test(value.bottom) ? value.bottom : DEFAULT_SPACING.bottom,
    left: typeof value.left === 'string' && PIXEL_LENGTH.test(value.left) ? value.left : DEFAULT_SPACING.left,
  };
}

function resolveBackground(value: unknown): BackgroundValue {
  if (!isRecord(value)) return DEFAULT_GRID_BACKGROUND;

  return {
    color: typeof value.color === 'string' ? value.color : DEFAULT_GRID_BACKGROUND.color,
    image: typeof value.image === 'string' ? value.image : DEFAULT_GRID_BACKGROUND.image,
    size:
      typeof value.size === 'string' && BACKGROUND_SIZE_VALUES.has(value.size)
        ? value.size
        : DEFAULT_GRID_BACKGROUND.size,
    position:
      typeof value.position === 'string' && BACKGROUND_POSITION_VALUES.has(value.position)
        ? value.position
        : DEFAULT_GRID_BACKGROUND.position,
    repeat:
      typeof value.repeat === 'string' && BACKGROUND_REPEAT_VALUES.has(value.repeat)
        ? value.repeat
        : DEFAULT_GRID_BACKGROUND.repeat,
  };
}

function resolveBorder(value: unknown): BorderValue {
  if (!isRecord(value) || !isRecord(value.color)) return DEFAULT_BORDER;

  let { color }: { color: BorderColor } = DEFAULT_BORDER;
  if (value.color.style === 'none' || value.color.style === 'theme') {
    color = { style: value.color.style };
  } else if (
    value.color.style === 'custom' &&
    typeof value.color.hex === 'string' &&
    COMPLETE_HEX.test(value.color.hex)
  ) {
    color = { style: 'custom', hex: value.color.hex };
  }

  return {
    color,
    width: typeof value.width === 'string' && PIXEL_LENGTH.test(value.width) ? value.width : DEFAULT_BORDER.width,
  };
}

export type GridLayoutProps = {
  /** Puck replaces the persisted slot array with this component before render. */
  content?: React.FC<SlotProps>;
  columns: string;
  mobileColumns: string;
  gap: BlockSpacing;
  alignItems: string;
  justifyItems: string;
  width: string;
  maxWidth: string;
  padding: BoxSpacing;
  margin: BoxSpacing;
  background: BackgroundValue;
  backgroundOpacity: number;
  textColor: string;
  border: BorderValue;
};

/**
 * The slot is the grid. Keeping the children in one real Puck drop zone preserves a single ordered
 * source of truth while CSS alone changes the number of visual columns.
 */
export const StyledGridSlot = styled.div`
  display: grid;
  grid-template-columns: var(--cms-grid-template-columns, repeat(3, minmax(0, 1fr)));
  min-width: 0;

  & > * {
    min-width: 0;
  }

  &[data-puck-dropzone]:empty {
    min-width: min(100%, var(--dcm-cms-grid-empty-min-width, 128px));
  }

  @media ${mediaQueries.largePhone} {
    grid-template-columns: var(--cms-grid-mobile-template-columns, repeat(3, minmax(0, 1fr)));
  }
`;

export const gridLayoutConfig: ComponentConfig<GridLayoutProps> = {
  label: 'Grid Layout',
  ai: {
    instructions:
      'A responsive CSS Grid container with equal-width columns. Add child components into its single content slot. Use child order for reading, keyboard, and visual order; do not simulate cells with repeated components or duplicate content.',
  },
  fields: {
    content: { type: 'slot' },
    background: {
      type: 'custom',
      label: 'Background',
      render: BackgroundField,
      ai: {
        schema: BACKGROUND_AI_SCHEMA as Record<string, unknown>,
        instructions:
          "Background styling. Prefer a theme surface role ('none', 'surface', 'muted') to a fixed hex value.",
      },
    },
    backgroundOpacity: BACKGROUND_OPACITY_FIELD,
    textColor: {
      type: 'custom',
      label: 'Text color',
      render: ContainerTextColorField,
      ai: {
        schema: (CONTAINER_TEXT_COLOR_AI_SCHEMA as unknown) as Record<string, unknown>,
        instructions:
          "Text color applied to descendants. Leave as 'inherit' unless the grid background needs an explicit readable contrast.",
      },
    },
    border: {
      type: 'object',
      label: 'Border',
      objectFields: {
        color: {
          type: 'custom',
          label: 'Color',
          render: BorderColorField,
          ai: {
            schema: BORDER_COLOR_AI_SCHEMA as Record<string, unknown>,
            instructions: "Prefer 'theme' or 'none' so the border follows merchant branding.",
          },
        },
        width: {
          type: 'custom',
          label: 'Width',
          render: NumberField('px', DEFAULT_BORDER.width),
          ai: { schema: PX_AI_SCHEMA, instructions: 'Border width, e.g. 1px.' },
        },
      },
    },
    columns: selectField(
      'Desktop columns',
      COLUMN_OPTIONS,
      'Number of equal-width columns on desktop. Choose a value from 1 through 6.'
    ),
    mobileColumns: selectField(
      'Mobile columns',
      MOBILE_COLUMN_OPTIONS,
      "Use 'inherit' to keep the desktop column count on phones, or choose an explicit 1–6 column override."
    ),
    gap: {
      type: 'select',
      label: 'Gap',
      options: GAP_OPTIONS,
      ai: {
        schema: { type: 'string', enum: GAP_OPTIONS.map(({ value }) => value) },
        instructions: "Uniform row and column gap from the shared spacing scale. 'sm' is the default.",
      },
    },
    alignItems: selectField(
      'Vertical alignment',
      ALIGNMENT_OPTIONS,
      "Alignment of children within each row. 'stretch' fills the available cell height."
    ),
    justifyItems: selectField(
      'Horizontal alignment',
      ALIGNMENT_OPTIONS,
      "Alignment of children within each column. 'stretch' fills the available cell width."
    ),
    width: {
      type: 'select',
      label: 'Width',
      options: [...WIDTH_OPTIONS],
      ai: {
        schema: { type: 'string', enum: Object.keys(BLOCK_WIDTHS) },
        instructions: "'fill' uses the available width; 'fit' shrinks to the grid's content.",
      },
    },
    maxWidth: selectField(
      'Max width',
      MAX_WIDTH_OPTIONS,
      "Caps the grid using the shared container scale. 'none' leaves it uncapped."
    ),
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: createSpacingFields(),
    },
    margin: {
      type: 'object',
      label: 'Margin',
      objectFields: createSpacingFields(),
    },
  },
  resolveFields: (data, { fields }) =>
    resolveBackgroundOpacityFields(backgroundValueColor(data.props.background), fields),
  defaultProps: {
    content: (EMPTY_GRID_CONTENT as unknown) as React.FC<SlotProps>,
    columns: DEFAULT_COLUMNS,
    mobileColumns: DEFAULT_MOBILE_COLUMNS,
    gap: DEFAULT_GAP,
    alignItems: DEFAULT_ALIGNMENT,
    justifyItems: DEFAULT_ALIGNMENT,
    width: DEFAULT_WIDTH,
    maxWidth: DEFAULT_MAX_WIDTH,
    padding: DEFAULT_SPACING,
    margin: DEFAULT_SPACING,
    background: DEFAULT_GRID_BACKGROUND,
    backgroundOpacity: DEFAULT_BACKGROUND_OPACITY,
    textColor: INHERIT_COLOR,
    border: DEFAULT_BORDER,
  },
  render: ({
    content: slotContent,
    columns = DEFAULT_COLUMNS,
    mobileColumns = DEFAULT_MOBILE_COLUMNS,
    gap = DEFAULT_GAP,
    alignItems = DEFAULT_ALIGNMENT,
    justifyItems = DEFAULT_ALIGNMENT,
    width = DEFAULT_WIDTH,
    maxWidth = DEFAULT_MAX_WIDTH,
    padding = DEFAULT_SPACING,
    margin = DEFAULT_SPACING,
    background = DEFAULT_GRID_BACKGROUND,
    backgroundOpacity = DEFAULT_BACKGROUND_OPACITY,
    textColor = INHERIT_COLOR,
    border = DEFAULT_BORDER,
  }) => {
    if (!slotContent) return <></>;
    const Slot = slotContent;

    const desktopColumnCount = resolveColumns(columns, Number(DEFAULT_COLUMNS));
    const mobileColumnCount =
      mobileColumns === 'inherit' ? desktopColumnCount : resolveColumns(mobileColumns, Number(DEFAULT_MOBILE_COLUMNS));
    const gridTemplateStyle = {
      '--cms-grid-template-columns': `repeat(${desktopColumnCount}, minmax(0, 1fr))`,
      '--cms-grid-mobile-template-columns': `repeat(${mobileColumnCount}, minmax(0, 1fr))`,
    } as React.CSSProperties;
    const resolvedGap = resolveGap(gap);
    const resolvedWidth =
      typeof width === 'string'
        ? lookup(BLOCK_WIDTHS, width, BLOCK_WIDTHS[DEFAULT_WIDTH])
        : BLOCK_WIDTHS[DEFAULT_WIDTH];
    const resolvedMaxWidth =
      typeof maxWidth === 'string'
        ? lookup(CONTAINER_WIDTHS, maxWidth, CONTAINER_WIDTHS[DEFAULT_MAX_WIDTH])
        : CONTAINER_WIDTHS[DEFAULT_MAX_WIDTH];

    const slotStyle: React.CSSProperties = {
      ...backgroundStyleWithOpacity(resolveBackground(background), backgroundOpacity),
      ...containerColorStyle(typeof textColor === 'string' ? textColor : INHERIT_COLOR),
      ...borderStyle(resolveBorder(border)),
      ...gridTemplateStyle,
      gap: resolvedGap,
      alignItems: resolveAlignment(alignItems),
      justifyItems: resolveAlignment(justifyItems),
      width: resolvedWidth,
      maxWidth: resolvedMaxWidth,
      padding: spacingToCss(resolveSpacing(padding)),
      margin: '0 auto',
      boxSizing: 'border-box',
    };

    return (
      <div style={{ margin: spacingToCss(resolveSpacing(margin)), minWidth: 0 }}>
        <Slot as={StyledGridSlot} minEmptyHeight={128} style={slotStyle} />
      </div>
    );
  },
};
